import React, { useState, useRef, useEffect, useMemo } from 'react';
import { ArrowLeft, Plus, UploadCloud, FileSpreadsheet, Trash2, Loader2, AlertCircle, Truck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import styles from './AddProduct.module.css';
import { SMIIFY_API } from '../../../config/api';
import { useAuth } from '../../../context/AuthContext';
import { vendorFetch, MODES, ymd, inr2 } from '../../../utils/vendorApi';

// small styles for the vendor box (the rest of the page uses AddProduct.module.css)
const vbox = { marginBottom: 18, padding: 12, border: '1px solid var(--h-color-neutral-200, #dedee2)', borderRadius: 10, background: '#fff', display: 'flex', flexDirection: 'column', gap: 8 };
const vlabel = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11.5, fontWeight: 800, color: 'var(--h-color-neutral-700, #36363b)' };
const vinput = { height: 32, padding: '0 8px', border: '1px solid var(--h-color-neutral-200, #dedee2)', borderRadius: 7, font: 'inherit', fontSize: 12.5, fontWeight: 700, background: '#fff' };

const BULK_ADD_API = `${SMIIFY_API}/inventory/bulk-add`;

const getEmptyRow = () => ({
    id: Date.now() + Math.random(),
    brand: '', productName: '', sku: '', category: '',
    measureUnit: 'piece', size: '', usageType: 'RETAIL',
    purchasePrice: '', mrp: '', sellingPrice: '',
    taxRate: '0', hsnCode: '',
    currentQuantity: '', lowStockAlertAt: '',
});

const AddProduct = () => {
    const navigate = useNavigate();
    const fileInputRef = useRef(null);

    const [isUploading, setIsUploading] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [rows, setRows] = useState([getEmptyRow()]);

    // ---- optional: everything here was bought from one vendor -> one purchase bill ----
    const { user, hasPermission } = useAuth();
    const owner = user?.role === 'SUPERADMIN';
    const canVendor = owner || hasPermission('vendors', 'record_purchase');
    const canPayVendor = owner || hasPermission('vendors', 'record_payment');
    const [vendors, setVendors] = useState([]);
    const [vendorId, setVendorId] = useState('');
    const [billNo, setBillNo] = useState('');
    const [billDate, setBillDate] = useState(ymd());
    const [gstOnPurchase, setGstOnPurchase] = useState(true);
    const [payMode, setPayMode] = useState('LATER');      // LATER or a mode (paid now in full)
    useEffect(() => {
        if (!canVendor) return;
        vendorFetch('', { storeId: localStorage.getItem('smiify_active_store_id') })
            .then((j) => setVendors((j.data || []).filter((v) => v.isActive !== false)))
            .catch(() => setVendors([]));
    }, [canVendor]);
    const billPreview = useMemo(() => {
        let total = 0; let gst = 0; let lines = 0; let noCost = 0;
        rows.forEach((r) => {
            const qty = Math.max(0, Math.floor(Number(r.currentQuantity) || 0));
            if (!r.productName?.trim() || !qty) return;
            const cost = Math.max(0, Number(r.purchasePrice) || 0);
            if (!cost) noCost += 1;
            const taxable = qty * cost;
            const g = gstOnPurchase ? taxable * (Number(r.taxRate) || 0) / 100 : 0;
            total += taxable + g; gst += g; lines += 1;
        });
        return { total: Math.round(total), gst, lines, noCost };
    }, [rows, gstOnPurchase]);

    const handleAddRow = () => setRows((r) => [...r, getEmptyRow()]);
    const handleRemoveRow = (id) => setRows((r) => (r.length > 1 ? r.filter((row) => row.id !== id) : r));
    const handleChange = (id, field, value) => setRows((r) => r.map((row) => (row.id === id ? { ...row, [field]: value } : row)));

    /* ----------------------------- save to server ----------------------------- */
    const sendToBackend = async (payload) => {
        const token = localStorage.getItem('smiify_token');
        const storeId = localStorage.getItem('smiify_active_store_id');
        if (!token) { alert('Your session has expired. Please log in again.'); return; }
        if (!storeId || storeId === 'global_hq') { alert('Select a branch from the header before adding products.'); return; }

        setIsUploading(true);
        try {
            const response = await fetch(BULK_ADD_API, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, 'x-store-id': storeId },
                body: JSON.stringify({
                    storeId,
                    products: payload,
                    ...(vendorId ? {
                        vendorPurchase: {
                            vendorId, billNo: billNo.trim(), billDate, gstOnPurchase,
                            ...(payMode !== 'LATER' && billPreview.total > 0 ? { payNow: { amount: billPreview.total, mode: payMode } } : {}),
                        },
                    } : {}),
                }),
            });
            const data = await response.json().catch(() => ({}));

            if (response.status === 201 || (response.ok && response.status !== 207)) {
                alert(data.message || 'Products added.');
                navigate('/inventory/overview');
            } else if (response.status === 207) {
                const skipped = [...(data.skippedNames || []), ...(data.skippedSkus || [])];
                alert(`${data.message}${skipped.length ? `\nSkipped (already exist): ${skipped.join(', ')}` : ''}`);
                navigate('/inventory/overview');
            } else {
                alert(data.message || 'Could not save products.');
            }
        } catch (error) {
            alert('Cannot reach the server. Check your connection and try again.');
        } finally {
            setIsUploading(false);
        }
    };

    /* ----------------------------- save grid ----------------------------- */
    const handleSaveGrid = () => {
        const validRows = rows.filter((r) => r.productName && r.productName.trim() !== '');
        if (validRows.length === 0) { alert('Enter at least the product name for one item.'); return; }

        const seen = new Set();
        const duplicates = [];
        validRows.forEach((row) => {
            const key = row.productName.trim().toLowerCase();
            if (seen.has(key)) duplicates.push(row.productName);
            seen.add(key);
        });
        if (duplicates.length) { alert(`These names appear twice in the grid:\n${duplicates.join(', ')}\n\nFix them before saving.`); return; }
        if (vendorId && billPreview.noCost > 0 && !window.confirm(`${billPreview.noCost} ${billPreview.noCost === 1 ? 'product has' : 'products have'} a quantity but no cost, so ${billPreview.noCost === 1 ? 'it goes' : 'they go'} on the vendor bill at ₹0 (products that already exist use their last cost). Save anyway?`)) return;

        sendToBackend(validRows.map((row) => ({
            usageType: row.usageType || 'RETAIL',
            basicInfo: {
                brand: row.brand || 'General',
                productName: row.productName.trim(),
                category: row.category || 'General',
                sku: row.sku,
                measureUnit: row.measureUnit || 'piece',
                size: Number(row.size) || 0,
            },
            pricing: {
                purchasePrice: Number(row.purchasePrice) || 0,
                mrp: Number(row.mrp) || 0,
                sellingPrice: Number(row.sellingPrice) || Number(row.mrp) || 0,
                hsnCode: row.hsnCode || 'N/A',
                taxRate: Number(row.taxRate) || 0,
            },
            stock: {
                currentQuantity: Number(row.currentQuantity) || 0,
                lowStockAlertAt: row.lowStockAlertAt === '' ? 5 : Number(row.lowStockAlertAt) || 0,
            },
        })));
    };

    /* ----------------------------- Excel / CSV ----------------------------- */
    const processFile = (file) => {
        if (!file) return;
        const ext = file.name.split('.').pop().toLowerCase();
        if (!['csv', 'xlsx', 'xls'].includes(ext)) { alert('Upload an Excel (.xlsx, .xls) or CSV file.'); return; }

        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const workbook = XLSX.read(new Uint8Array(e.target.result), { type: 'array' });
                const sheet = workbook.Sheets[workbook.SheetNames[0]];
                const rowsArray = XLSX.utils.sheet_to_json(sheet, { header: 1 });

                if (rowsArray.length <= 1) { alert('The file is empty or has only headers.'); return; }
                if (rowsArray.length > 301) { alert('Up to 300 products per upload.'); return; }

                const str = (v) => (v != null ? String(v).trim() : '');
                const newRows = [];
                const seen = new Set();
                let duplicatesSkipped = 0;

                for (let i = 1; i < rowsArray.length; i += 1) {
                    const row = rowsArray[i];
                    if (!row || row.length === 0) continue;
                    const name = str(row[1]);
                    if (!name) continue;
                    if (seen.has(name.toLowerCase())) { duplicatesSkipped += 1; continue; }
                    seen.add(name.toLowerCase());
                    newRows.push({
                        id: Date.now() + Math.random() + i,
                        brand: str(row[0]), productName: name, sku: str(row[2]), category: str(row[3]),
                        measureUnit: str(row[4]).toLowerCase() || 'piece', size: str(row[5]),
                        usageType: str(row[6]).toUpperCase() || 'RETAIL',
                        purchasePrice: str(row[7]), mrp: str(row[8]), sellingPrice: str(row[9]),
                        hsnCode: str(row[10]), taxRate: str(row[11]) || '0',
                        currentQuantity: str(row[12]), lowStockAlertAt: str(row[13]) || '5',
                    });
                }

                if (newRows.length) {
                    setRows(newRows);
                    alert(`Loaded ${newRows.length} products into the grid.${duplicatesSkipped ? `\nSkipped ${duplicatesSkipped} duplicate names.` : ''}\n\nCheck the data, then click Save.`);
                } else {
                    alert("No products found. Make sure the 'Product Name' column is filled.");
                }
            } catch (err) {
                alert('Could not read the file. Make sure it is a valid Excel or CSV file.');
            }
        };
        reader.readAsArrayBuffer(file);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
    const handleDragLeave = (e) => { e.preventDefault(); setIsDragging(false); };
    const handleDrop = (e) => { e.preventDefault(); setIsDragging(false); processFile(e.dataTransfer.files[0]); };

    const downloadTemplate = (e) => {
        e.preventDefault();
        const headers = 'Brand,Product Name (Required),SKU (Optional),Category,Unit,Size,Usage Type,Cost,MRP,Selling Price,HSN Code,GST Rate,Initial Stock,Min Alert\n';
        const sample = "L'Oreal,Repair Shampoo,,Hair Care,ml,300,RETAIL,450,750,700,330510,18,10,5\n";
        const a = document.createElement('a');
        a.href = window.URL.createObjectURL(new Blob([headers + sample], { type: 'text/csv' }));
        a.download = 'Inventory_Template.csv';
        a.click();
    };

    const filledCount = rows.filter((r) => r.productName).length;

    return (
        <div className={styles.pageWrapper}>
            <div className={styles.fixedHeader}>
                <div className={styles.headerLeft}>
                    <button type="button" className={styles.backBtn} onClick={() => navigate('/inventory/overview')} aria-label="Back"><ArrowLeft size={16} /></button>
                    <div>
                        <h1 className={styles.pageTitle}>Create Product Master</h1>
                        <p className={styles.pageSubtitle}>Enter manually or upload Excel/CSV. Opening stock is saved in each product's stock history.</p>
                    </div>
                </div>
                <div className={styles.headerActions}>
                    <button type="button" className={styles.secondaryBtn} onClick={() => navigate('/inventory/overview')} disabled={isUploading}>Cancel</button>
                    <button type="button" className={styles.primaryBtn} onClick={handleSaveGrid} disabled={isUploading || filledCount === 0}>
                        {isUploading ? <><Loader2 size={14} className="animate-spin" /> Saving...</> : vendorId ? `Save ${filledCount} products + vendor bill` : `Save ${filledCount} Records`}
                    </button>
                </div>
            </div>

            <div className={styles.contentSplit}>
                <div className={styles.leftGrid}>
                    <div className={styles.gridHeader}>
                        <span className={styles.sectionTitle}>Review & Edit Data</span>
                        <button type="button" className={styles.secondaryBtnSmall} onClick={handleAddRow} disabled={isUploading}><Plus size={12} /> Add Row</button>
                    </div>
                    <div className={styles.tableContainer}>
                        <table className={styles.excelTable}>
                            <thead>
                                <tr>
                                    <th>Brand</th><th>Product Name*</th><th>SKU (Opt)</th><th>Category</th>
                                    <th className={styles.wSmall}>Unit</th><th className={styles.wSmall}>Size</th>
                                    <th>Usage Type</th><th className={styles.wSmall}>Cost</th>
                                    <th className={styles.wSmall}>MRP</th><th className={styles.wSmall}>Sell</th>
                                    <th className={styles.wSmall}>GST %</th><th>HSN</th>
                                    <th className={styles.wSmall}>Stock</th><th className={styles.wSmall}>Min</th>
                                    <th className={styles.wTiny}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row) => (
                                    <tr key={row.id}>
                                        <td><input type="text" value={row.brand} onChange={(e) => handleChange(row.id, 'brand', e.target.value)} className={styles.cellInput} disabled={isUploading} /></td>
                                        <td><input type="text" value={row.productName} onChange={(e) => handleChange(row.id, 'productName', e.target.value)} className={styles.cellInput} placeholder="Required" disabled={isUploading} /></td>
                                        <td><input type="text" value={row.sku} onChange={(e) => handleChange(row.id, 'sku', e.target.value)} className={styles.cellInput} disabled={isUploading} /></td>
                                        <td><input type="text" value={row.category} onChange={(e) => handleChange(row.id, 'category', e.target.value)} className={styles.cellInput} disabled={isUploading} /></td>
                                        <td>
                                            <select value={row.measureUnit} onChange={(e) => handleChange(row.id, 'measureUnit', e.target.value)} className={styles.cellSelect} disabled={isUploading}>
                                                <option value="piece">pc</option><option value="ml">ml</option><option value="gm">gm</option><option value="liter">L</option><option value="kg">kg</option>
                                            </select>
                                        </td>
                                        <td><input type="number" min="0" value={row.size} onChange={(e) => handleChange(row.id, 'size', e.target.value)} className={styles.cellInput} disabled={isUploading} /></td>
                                        <td>
                                            <select value={row.usageType} onChange={(e) => handleChange(row.id, 'usageType', e.target.value)} className={styles.cellSelect} disabled={isUploading}>
                                                <option value="RETAIL">Retail</option><option value="PROFESSIONAL">Pro</option><option value="HYBRID">Hybrid</option><option value="CONSUMABLE">Consumable</option><option value="SAMPLE">Sample</option>
                                            </select>
                                        </td>
                                        <td><input type="number" min="0" value={row.purchasePrice} onChange={(e) => handleChange(row.id, 'purchasePrice', e.target.value)} className={styles.cellInput} disabled={isUploading} /></td>
                                        <td><input type="number" min="0" value={row.mrp} onChange={(e) => handleChange(row.id, 'mrp', e.target.value)} className={styles.cellInput} disabled={isUploading} /></td>
                                        <td><input type="number" min="0" value={row.sellingPrice} onChange={(e) => handleChange(row.id, 'sellingPrice', e.target.value)} className={styles.cellInput} disabled={isUploading} /></td>
                                        <td>
                                            <select value={row.taxRate} onChange={(e) => handleChange(row.id, 'taxRate', e.target.value)} className={styles.cellSelect} disabled={isUploading}>
                                                <option value="0">0</option><option value="5">5</option><option value="12">12</option><option value="18">18</option><option value="28">28</option>
                                            </select>
                                        </td>
                                        <td><input type="text" value={row.hsnCode} onChange={(e) => handleChange(row.id, 'hsnCode', e.target.value)} className={styles.cellInput} disabled={isUploading} /></td>
                                        <td><input type="number" min="0" value={row.currentQuantity} onChange={(e) => handleChange(row.id, 'currentQuantity', e.target.value)} className={styles.cellInput} disabled={isUploading} /></td>
                                        <td><input type="number" min="0" value={row.lowStockAlertAt} onChange={(e) => handleChange(row.id, 'lowStockAlertAt', e.target.value)} className={styles.cellInput} disabled={isUploading} /></td>
                                        <td className={styles.actionCell}>
                                            <button type="button" className={styles.rowDeleteBtn} onClick={() => handleRemoveRow(row.id)} disabled={rows.length === 1 || isUploading} aria-label="Remove row"><Trash2 size={12} /></button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className={styles.rightPanel}>
                    {canVendor && (
                        <div style={vbox}>
                            <span className={styles.sectionTitle} style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Truck size={14} /> Bought from a vendor?</span>
                            <label style={vlabel}>
                                Vendor
                                <select style={vinput} value={vendorId} onChange={(e) => setVendorId(e.target.value)} disabled={isUploading}>
                                    <option value="">No vendor (opening stock)</option>
                                    {vendors.map((v) => <option key={v._id} value={v._id}>{v.name}</option>)}
                                </select>
                            </label>
                            {!vendorId && <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--h-color-neutral-500, #797980)' }}>Choose the vendor if this stock is a delivery: one purchase bill is saved, it shows in Expenses and in the vendor's khata.{vendors.length === 0 ? ' Add vendors in Inventory > Vendors.' : ''}</span>}
                            {vendorId && (
                                <>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                                        <label style={vlabel}>Vendor bill no.<input style={vinput} value={billNo} onChange={(e) => setBillNo(e.target.value)} maxLength={60} placeholder="Optional" disabled={isUploading} /></label>
                                        <label style={vlabel}>Bill date<input style={vinput} type="date" value={billDate} max={ymd()} onChange={(e) => setBillDate(e.target.value)} disabled={isUploading} /></label>
                                    </div>
                                    <label style={{ ...vlabel, flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                                        <input type="checkbox" checked={gstOnPurchase} onChange={(e) => setGstOnPurchase(e.target.checked)} disabled={isUploading} />
                                        Vendor charged GST (each product's GST %)
                                    </label>
                                    <label style={vlabel}>
                                        Payment
                                        <select style={vinput} value={payMode} onChange={(e) => setPayMode(e.target.value)} disabled={isUploading || !canPayVendor}>
                                            <option value="LATER">Pay later (udhaar)</option>
                                            {canPayVendor && MODES.map((m) => <option key={m.value} value={m.value}>Paid now: {m.label}</option>)}
                                        </select>
                                    </label>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 10px', borderRadius: 8, background: 'var(--h-color-neutral-50, #f8f9fa)', fontSize: 12.5, fontWeight: 800 }}>
                                        <span>Bill (qty × cost){billPreview.gst > 0 ? ` incl. GST ${inr2(billPreview.gst)}` : ''}</span>
                                        <b>{inr2(billPreview.total)}</b>
                                    </div>
                                    <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--h-color-neutral-500, #797980)', lineHeight: 1.45 }}>
                                        Stock comes in on this bill. Products that already exist are not created again: their quantity is added on the same bill.
                                        {billPreview.noCost > 0 && <b style={{ color: '#9b4600' }}> {billPreview.noCost} with quantity but no cost.</b>}
                                    </span>
                                </>
                            )}
                        </div>
                    )}
                    <span className={styles.sectionTitle}>Bulk Upload (Excel / CSV)</span>
                    <input type="file" accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel" ref={fileInputRef} style={{ display: 'none' }} onChange={(e) => processFile(e.target.files[0])} />

                    <div
                        className={`${styles.uploadBox} ${isDragging ? styles.dragActive : ''} ${isUploading ? styles.uploading : ''}`}
                        onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}
                        onClick={() => !isUploading && fileInputRef.current.click()}
                        role="button" tabIndex={0}
                        onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && !isUploading) fileInputRef.current.click(); }}
                    >
                        <UploadCloud size={32} className={styles.uploadIcon} />
                        <p className={styles.uploadText}>{isDragging ? 'Drop the file here' : 'Upload Excel/CSV to fill the grid'}</p>
                    </div>

                    <div className={styles.downloadTemplate}>
                        <FileSpreadsheet size={16} />
                        <div>
                            <p className={styles.templateText}>Need the correct format?</p>
                            <button type="button" className={styles.templateLink} onClick={downloadTemplate} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>Download sample format</button>
                        </div>
                    </div>

                    <div style={{ marginTop: '20px', padding: '12px', background: 'var(--h-color-primary-50, #f5f6ff)', border: '1px solid var(--h-color-primary-200, #d9dbff)', borderRadius: '8px', fontSize: '12px', color: 'var(--h-color-primary-800, #331c74)', display: 'flex', gap: '8px', lineHeight: 1.45 }}>
                        <AlertCircle size={16} style={{ flexShrink: 0 }} />
                        <span>Uploading a file replaces the grid. Duplicate names in the sheet are removed automatically. Products that already exist are skipped, unless a vendor is chosen: then their quantity is added on the vendor bill.</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddProduct;
